import { useMemo } from 'react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import type {
  ReportDefinitionInput,
  ReportOutputFormat,
  ReportResult,
  ReportRun,
  ReportSchedule,
  SavedReport,
} from './reporting-types';

/**
 * ADR-0013 client. Every call here goes through `/reporting/*`, the report
 * builder's own controller — a different surface from `/analytics/*`, which
 * `analytics.ts` already covers for the fixed dashboard and the ad-hoc query
 * picker. The two share nothing but the registries both compile through.
 */

// -------------------------------------------------------------- saved reports

export interface SavedReportListParams {
  page?: number;
  limit?: number;
  search?: string;
}

export function useSavedReports(params: SavedReportListParams) {
  const { data, loading, error, reload } = useApi<unknown>('/reporting/reports', params as Record<string, unknown>);
  const { rows, total } = useMemo(() => rowsOf<SavedReport>(data), [data]);
  return { reports: rows, total, loading, error, reload };
}

export function useSavedReport(id: string | null) {
  return useApi<SavedReport>(id ? `/reporting/reports/${id}` : null);
}

export interface SavedReportPayload {
  name: string;
  description?: string;
  definition: ReportDefinitionInput;
  isShared?: boolean;
}

export function createSavedReport(body: SavedReportPayload) {
  return api.post<SavedReport>('/reporting/reports', body).then((r) => r.data);
}

export function updateSavedReport(id: string, body: Partial<SavedReportPayload>) {
  return api.patch<SavedReport>(`/reporting/reports/${id}`, body).then((r) => r.data);
}

export function deleteSavedReport(id: string) {
  return api.delete<{ id: string; suspended: number }>(`/reporting/reports/${id}`).then((r) => r.data);
}

/** The picker's "preview" — runs a definition without saving it. */
export function previewReport(definition: ReportDefinitionInput) {
  return api.post<ReportResult>('/reporting/preview', { definition }).then((r) => r.data);
}

// ------------------------------------------------------------------- export

/** A blob response plus the filename the server chose, via `Content-Disposition`. */
export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

function filenameFrom(headers: Record<string, unknown>, fallback: string): string {
  const raw = headers?.['content-disposition'];
  if (typeof raw === 'string') {
    const match = /filename="([^"]+)"/.exec(raw);
    if (match) return match[1];
  }
  return fallback;
}

export async function exportDefinition(
  definition: ReportDefinitionInput,
  format: ReportOutputFormat,
  name: string,
): Promise<DownloadedFile> {
  const response = await api.post(
    '/reporting/export',
    { definition, format, name },
    { responseType: 'blob' },
  );
  return {
    blob: response.data as Blob,
    filename: filenameFrom(response.headers as Record<string, unknown>, `${name}.${format.toLowerCase()}`),
  };
}

export async function exportSavedReport(
  id: string,
  format: ReportOutputFormat,
  fallbackName: string,
): Promise<DownloadedFile> {
  const response = await api.post(`/reporting/reports/${id}/export`, undefined, {
    params: { format },
    responseType: 'blob',
  });
  return {
    blob: response.data as Blob,
    filename: filenameFrom(
      response.headers as Record<string, unknown>,
      `${fallbackName}.${format.toLowerCase()}`,
    ),
  };
}

/** Triggers the browser's own save dialog for a blob already downloaded. */
export function saveBlob(file: DownloadedFile): void {
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.filename;
  link.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------- schedules

export interface ScheduleListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  savedReportId?: string;
}

export function useSchedules(params: ScheduleListParams) {
  const { data, loading, error, reload } = useApi<unknown>('/reporting/schedules', params as Record<string, unknown>);
  const { rows, total } = useMemo(() => rowsOf<ReportSchedule>(data), [data]);
  return { schedules: rows, total, loading, error, reload };
}

export interface SchedulePayload {
  name: string;
  savedReportId: string;
  frequency: string;
  hour: number;
  minute?: number;
  dayOfWeek?: number;
  dayOfMonth?: number;
  timezone?: string;
  format?: ReportOutputFormat;
  linkTtlHours?: number;
  recipientUserIds?: string[];
}

export function createSchedule(body: SchedulePayload) {
  return api.post<ReportSchedule>('/reporting/schedules', body).then((r) => r.data);
}

export interface ScheduleUpdatePayload {
  name?: string;
  frequency?: string;
  hour?: number;
  minute?: number;
  dayOfWeek?: number;
  dayOfMonth?: number;
  timezone?: string;
  format?: ReportOutputFormat;
  linkTtlHours?: number;
  status?: string;
}

export function updateSchedule(id: string, body: ScheduleUpdatePayload) {
  return api.patch<ReportSchedule>(`/reporting/schedules/${id}`, body).then((r) => r.data);
}

export function deleteSchedule(id: string) {
  return api.delete<{ id: string }>(`/reporting/schedules/${id}`).then((r) => r.data);
}

export function addScheduleRecipients(id: string, userIds: string[]) {
  return api.post<ReportSchedule>(`/reporting/schedules/${id}/recipients`, { userIds }).then((r) => r.data);
}

export function removeScheduleRecipient(id: string, userId: string) {
  return api
    .delete<ReportSchedule>(`/reporting/schedules/${id}/recipients/${userId}`)
    .then((r) => r.data);
}

// -------------------------------------------------------------------- runs

export interface RunListParams {
  page?: number;
  limit?: number;
  savedReportId?: string;
  scheduleId?: string;
}

export function useReportRuns(params: RunListParams) {
  const { data, loading, error, reload } = useApi<unknown>('/reporting/runs', params as Record<string, unknown>);
  const { rows, total } = useMemo(() => rowsOf<ReportRun>(data), [data]);
  return { runs: rows, total, loading, error, reload };
}

/**
 * `GET /reporting/runs/:id/artifact` — no secret in the URL. The caller
 * authenticates as themselves and the server looks up whether a live grant
 * exists for (this run, this user); see ADR-0013 §2.8.
 */
export async function downloadRunArtifact(runId: string, fallbackName = 'report'): Promise<DownloadedFile> {
  const response = await api.get(`/reporting/runs/${runId}/artifact`, { responseType: 'blob' });
  return {
    blob: response.data as Blob,
    filename: filenameFrom(response.headers as Record<string, unknown>, fallbackName),
  };
}
